import { FC, useRef, useState } from "react";
import { useStyles } from "./OtpField.styles";
import { Text } from "@mantine/core";

type Props = {
  length: number;
  onChange: (value: string) => void;
  onEnter: (value: string) => void;
};

const OtpField: FC<Props> = ({ length, onChange, onEnter }) => {
  const { classes } = useStyles();
  const [values, setValues] = useState(Array(length).fill(""));
  const digitRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (e: any, index: number) => {
    const updatedInput = [...values];
    updatedInput[index] = e.target.value;
    setValues(updatedInput);
    onChange?.(updatedInput.join(""));

    if (e.target.value && index < length - 1) {
      digitRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      !e.currentTarget.value
    ) {
      if (values[index]) {
        const newValues = [...values];
        newValues[index] = "";
        setValues(newValues);
        onChange?.(newValues.join(""));
      }
      if (index > 0 && e.key === "Backspace") {
        digitRef.current[index - 1]?.focus();
      }
    }

    if (e.key === "Enter") {
      const otp = values.join("");
      if (otp.length !== length) {
        return;
      }
      onEnter?.(otp);
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain");
    if (pasteData.length === length && /^[0-9]{6}$/.test(pasteData)) {
      const formattedPasteData = pasteData.split("");
      const newValues = [...values];
      for (let i = 0; i < pasteData.length && i + index < length; i++) {
        newValues[i + index] = formattedPasteData[i];
      }
      setValues(newValues);
      onChange?.(newValues.join(""));
      const focusIndex = Math.min(pasteData.length, length - 1);
      digitRef.current[focusIndex]?.focus();
    }
  };

  return (
    <div className={classes.otpOuterSection}>
      <Text className={classes.otpHeading}>
        Enter the 6-digit code generated by your authenticator app.
      </Text>
      <div className={classes.otpInnerSection}>
        <div className={classes.otpWrapper}>
          {Array.from({ length }).map((_, index) => {
            return (
              <input
                key={index}
                value={values[index]}
                ref={(element) => (digitRef.current[index] = element)}
                pattern="\d*"
                maxLength={1}
                className={classes.otpDigit}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(e, index)}
              ></input>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OtpField;
